<script setup lang="ts">
import { ref, watch, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { defineRule, useField } from "vee-validate";
import { storeToRefs } from "pinia";
import SelectUSDT2Deposit from "@/components/menberFunction/depositPage/SelectUSDT2Deposit.vue";
import { useMemberService } from "@/stores/memberFunction/memberService";
import { useLogin } from "@/stores/login";
import { useCounterStore } from "@/stores/counter";
import type { MemberRechargePage2ListData } from "@/assets/API/APItype";
import triangle_icon from "@image/memberFunction/common/triangle_icon.png";
import 文字框_長_A from "@image/memberFunction/文字框/文字框_長_A.png";
import 驚嘆號icon from "@image/memberFunction/11/驚嘆號icon.png";

interface DepositFormData {
  payCount: number;
  aTunnel: string;
}
interface DepositPostData {
  nId: string;
  sPay_name: string;
  sNickname: string;
  nMax: string;
  nMin: string;
  sGids: string;
  nMoney: string;
  t: string;
  a: string;
  aTunnel: string;
  postUrl: string;
}
const { t } = useI18n();
const memberService = useMemberService();
const { DepositUSDTList, UsdtExchangeRate } = storeToRefs(memberService);
const login = useLogin();
const store = useCounterStore();

const props = defineProps<{
  Info: MemberRechargePage2ListData[];
}>();
const emit = defineEmits(["changeDepositPage"]);
function changeDepositPage(page: number) {
  emit("changeDepositPage", page);
}

const DepositForm = ref<any>(null);
const selectedIndex = ref(null);
const selectedATunnel = ref<string>("");
const selectedInfo = computed(() => selectedIndex.value !== null ? props.Info[selectedIndex.value] : null);

// 支付渠道 aTunnel
const PayATunnel = computed(() => {
  if (selectedInfo.value === null) return [];
  const payment = selectedInfo.value.aPayment[parseInt(selectedInfo.value.aPayment.nId)];
  console.log("PayATunnel:", payment);
  if (payment && typeof payment === "object" && "aTunnel" in payment) {
    return payment.aTunnel;
  }
  return [];
});

const CanDepositForm = computed(() => {
  if (DepositUSDTList.value.length <= 0) return false;
  const hasUnCheck = DepositUSDTList.value.every((list) => {
    return list.nStatus === "0";
  });
  return !hasUnCheck;
});

// 判斷USDT換算
const payCount = ref<number>(0);

const convertedUSDT = computed(() => {
  if (!payCount.value) return 0;
  const rate = UsdtExchangeRate.value;
  return Math.floor((payCount.value / rate) * 100) / 100; // 換算並無條件捨去到小數點第二位
});

// 快捷金额选项 - 使用动态数据
const countList = computed(() => {
  if (!selectedInfo.value?.aPayment?.aTopUpMoneyBtn) {
    return [1000, 3000, 5000, 10000, 20000, 50000]; // 默认值
  }
  return selectedInfo.value.aPayment.aTopUpMoneyBtn.map(item => Number(item));
});


// 监听金额变化进行验证
watch(
  () => payCount.value,
  (newValue) => {
    const numValue = Number(newValue);
    if (!selectedInfo.value) return;
    const { nMax, nMin } = selectedInfo.value.aPayment;
    
    // 检查最大最小值
    if (numValue > Number(nMax)) {
      DepositForm.value?.setFieldError("payCount", t('MemberFunction.Deposit.MaxAmount', { nMax }));
      return;
    } else if (numValue < Number(nMin)) {
      DepositForm.value?.setFieldError("payCount", t('MemberFunction.Deposit.MinAmount', { nMin }));
      return;
    }
    
    // 检查尾数限制
    const stintError = validateMoneyStint(numValue);
    if (stintError) {
      DepositForm.value?.setFieldError("payCount", stintError);
    } else {
      DepositForm.value?.setFieldError("payCount", "");
    }
  },
);

// 验证金额尾数限制
function validateMoneyStint(value: number): string | null {
  if (!selectedInfo.value?.aPayment?.aTopUpMoneyStint) {
    return null; // 没有限制规则
  }

  const stint = selectedInfo.value.aPayment.aTopUpMoneyStint;
  const valueStr = value.toString();
  const len = valueStr.length;

  // 获取各位数字
  const singleDigit = len >= 1 ? valueStr[len - 1] : '0';
  const tenDigit = len >= 2 ? valueStr[len - 2] : '0';
  const hundredDigit = len >= 3 ? valueStr[len - 3] : '0';

  // 验证个位数
  if (stint.sSingleDigits !== '*') {
    if (stint.sSingleDigits === '-0' && singleDigit === '0') {
      return t('MemberFunction.Deposit.SingleDigitCannotBeZero');
    } else if (stint.sSingleDigits.startsWith('+')) {
      const requiredDigit = stint.sSingleDigits.substring(1);
      if (singleDigit !== requiredDigit) {
        return t('MemberFunction.Deposit.SingleDigitMustBe', { digit: requiredDigit });
      }
    }
  }

  // 验证十位数
  if (stint.sTens !== '*') {
    if (stint.sTens === '-0' && tenDigit === '0') {
      return t('MemberFunction.Deposit.TenDigitCannotBeZero');
    } else if (stint.sTens.startsWith('+')) {
      const requiredDigit = stint.sTens.substring(1);
      if (tenDigit !== requiredDigit) {
        return t('MemberFunction.Deposit.TenDigitMustBe', { digit: requiredDigit });
      }
    }
  }

  // 验证百位数
  if (stint.sHundreds !== '*') {
    if (stint.sHundreds === '-0' && hundredDigit === '0') {
      return t('MemberFunction.Deposit.HundredDigitCannotBeZero');
    } else if (stint.sHundreds.startsWith('+')) {
      const requiredDigit = stint.sHundreds.substring(1);
      if (hundredDigit !== requiredDigit) {
        return t('MemberFunction.Deposit.HundredDigitMustBe', { digit: requiredDigit });
      }
    }
  }

  return null;
}

defineRule('unit_digit_zero', (value: number | string) => {
  const num = Number(value);
  // 使用动态验证函数
  const error = validateMoneyStint(num);
  if (error) {
    return error;
  }
  return true;
});

// 快捷金额设置函数
function setQuickAmount(amount: number) {
  if (!selectedInfo.value) return;
  payCount.value = amount;
}

// 添加千分位逗号函数
function addCommas(str: string) {
  return store.addCommas(str);
}

// 清除金额函数
function clearAmount() {
  payCount.value = 0;
}

function submit(VForm: DepositFormData) {
  if (!selectedInfo.value) return;
  const { nId, sPay_name, sNickname, nMax, nMin, sGids, t, a, postUrl } =
    selectedInfo.value.aPayment;
  const { payCount, aTunnel } = VForm;
  const postData: DepositPostData = {
    nId,
    sPay_name,
    sNickname,
    nMax,
    nMin,
    sGids,
    t: t.toString(),
    a,
    nMoney: payCount.toString(),
    aTunnel,
    postUrl,
  };
  DepositForm.value.resetForm();
  memberService.getMemberRecharge2(postData);
}
</script>

<template>
  <VForm @submit="submit" ref="DepositForm" class="py-[2em] px-[9vw]">
    <div class="text-primary mb-[3dvh]" v-if="CanDepositForm">
      <label for="payment" class="grid grid-cols-12 text-[14px] md:text-[16px] mt-[16px] md:mt-[3dvh]">
        <p class="mr-[1em] col-span-4">
          {{ $t("MemberFunction.Deposit.platform") }}
        </p>
        <div class="relative col-span-8">
          <div class="h-[26px] w-full">
            <img v-img-src="triangle_icon" alt=""
              class="h-[12px] absolute right-[2.5%] md:right-[3.4%] top-[13px] -translate-y-1/2 z-20" />
          </div>
          <select v-model="selectedIndex"
            class="absolute select-bg-border top-0 bg-transparent px-2 rounded w-full h-[26px] text-primary">
            <option class="text-black" :value="null">
              {{ $t("MemberFunction.Deposit.SelectPlatform") }}
            </option>
            <option class="text-black" v-for="(item, idx) in props.Info" :key="item.nId" :value="idx">
              {{ item.aPayment.sPay_name }}
            </option>
          </select>
        </div>
      </label>
      <label for="aTunnel" class="grid grid-cols-12 text-[14px] md:text-[16px] mt-[16px] md:mt-[3dvh]"
        v-if="JSON.stringify(PayATunnel) !== '{}'">
        <p class="mr-[1em] col-span-4">
          {{ $t("MemberFunction.Deposit.PaymentMethod") }}
        </p>
        <div class="relative col-span-8">
          <div class="h-[26px] w-full">
            <img v-img-src="triangle_icon" alt=""
              class="h-[12px] absolute right-[2.5%] md:right-[3.4%] top-[13px] -translate-y-1/2 z-20" />
          </div>
          <VField id="aTunnel" :label="$t('MemberFunction.Deposit.PaymentMethod')" name="aTunnel" v-slot="{ field }"
            class="relative" :rules="{ required: true }">
            <select v-bind="field" @change="selectedATunnel = ($event.target as HTMLSelectElement).value"
              class="absolute select-bg-border top-0 bg-transparent px-2 rounded w-full h-[26px] text-primary">
              <option class="text-black" value="">
                {{ $t("MemberFunction.Deposit.SelectPaymentMethod") }}
              </option>
              <option class="text-black" v-for="(value, key) in PayATunnel" :key="value + 'onlineDepositOther'"
                :value="key">
                {{ value }}
              </option>
            </select>
          </VField>
          <ErrorMessage name="StorePayment" class="text-primary text-[10px]" v-slot="{ message }">
            <p class="text-primary text-[10px] mr-[6px]">
              {{
                message.includes("必填")
                  ? $t("MemberFunction.Deposit.Require")
                  : message
              }}
            </p>
          </ErrorMessage>
        </div>
      </label>
      <label for="payCount" class="grid grid-cols-12 text-[14px] md:text-[16px] mt-[16px] md:mt-[3dvh]">
        <p class="mr-[1em] col-span-4">
          {{ $t("MemberFunction.Deposit.DepositAmount") }}
        </p>
        <div class="relative inline-block col-span-8">
          <div class="h-[26px]">
            <img v-img-src="文字框_長_A" alt="" class="h-[26px] w-full" />
          </div>
          <VField class="col-span-1 absolute h-[26px] top-0 rounded-md bg-transparent w-full px-2" id="payCount"
            :label="$t('MemberFunction.Deposit.DepositAmount')" name="payCount" type="number" v-model="payCount"
            :max="selectedInfo?.aPayment.nMax"
            :min="selectedInfo?.aPayment.nMin"
            :disabled="!selectedATunnel"
            :rules="{
              required: true,
              min_value: selectedInfo?.aPayment.nMin,
              max_value: selectedInfo?.aPayment.nMax,
              unit_digit_zero: true,
            }" />
          <p class="text-primary text-[12px] mr-[6px]">
            {{ $t("MemberFunction.Deposit.DepositAmount") }}:
            {{ selectedInfo?.aPayment.nMin }} ~
            {{ selectedInfo?.aPayment.nMax }}
            {{ $t("MemberFunction.Withdraw.TWD") }}
          </p>
          <ErrorMessage name="payCount" class="text-yellow-500 text-[12px]" v-slot="{ message }">
            <p class="text-yellow-500 text-[12px] mr-[6px] font-bold">
              {{
                message.includes("必填")
                  ? $t("MemberFunction.Deposit.Require")
                  : message
              }}
            </p>
          </ErrorMessage>
        </div>
      </label>
      <label class="grid grid-cols-12 text-[14px] md:text-[16px] mt-[16px] md:mt-[3dvh]">
        <p class="mr-[1em] col-span-4">USDT</p>
        <div class="relative inline-block col-span-8">
          <div class="h-[26px]">
            <img v-img-src="文字框_長_A" alt="" class="h-[26px] w-full" />
          </div>
          <div class="col-span-1 absolute h-[26px] top-0 rounded-md bg-transparent w-full px-2">
            {{ convertedUSDT }}
          </div>
        </div>
      </label>
    </div>
    <!-- 快捷金额按钮 -->
    <div class="mb-[3dvh]" v-if="selectedATunnel">
      <!-- 手机版：3x2 网格布局 -->
      <div class="grid grid-cols-3 gap-2 md:hidden">
        <button
          v-for="count in countList"
          :key="'USDTCountList' + count"
          type="button"
          class="btn-y-border w-full inline-block py-1 px-1"
          @click="setQuickAmount(count)"
        >
          <p class="text-[12px] font-bold text-secondary">
            {{ addCommas(count.toString()) }}
          </p>
        </button>
        <button
          type="button"
          class="btn-y-border w-full inline-block py-1 px-1"
          @click="clearAmount"
        >
          <p class="text-[12px] font-bold text-secondary">
            {{ $t("MemberFunction.Deposit.Clear") }}
          </p>
        </button>
      </div>
      <!-- 桌面版：水平布局 -->
      <ul class="hidden md:flex justify-between items-center">
        <li
          v-for="count in countList"
          :key="'USDTCountList' + count"
          class="mr-[3px] w-full"
        >
          <button
            type="button"
            class="btn-y-border w-full inline-block px-1"
            @click="setQuickAmount(count)"
          >
            <p class="text-[14px] font-bold text-secondary">
              {{ addCommas(count.toString()) }}
            </p>
          </button>
        </li>
        <li class="w-full">
          <button
            type="button"
            class="btn-y-border w-full inline-block"
            @click="clearAmount"
          >
            <p class="text-[14px] font-bold text-secondary">
              {{ $t("MemberFunction.Deposit.Clear") }}
            </p>
          </button>
        </li>
      </ul>
    </div>

    <div class="md:static flex flex-col justify-center items-center">
      <button type="submit" class="btn-y-border" v-if="CanDepositForm">
        <p class="text-[14px] md:text-[14px] font-bold text-secondary px-[2em] py-[.1em]">
          {{ $t("MemberFunction.Deposit.Next") }}
        </p>
      </button>
      <SelectUSDT2Deposit @changeDepositPage="changeDepositPage" />
      <div class="mt-[3dvh]">
        <div class="flex justify-center items-center leading-[36px]">
          <img v-img-src="驚嘆號icon" alt="" class="w-[16px] mr-1" />
          <span class="text-red-600">
            {{ $t("MemberFunction.Deposit.StoreNotice") }}
          </span>
        </div>
        <div class="text-primary text-[14px] md:text-[16px] leading-[20px]">
          <div class="mb-[1vh] flex">
            <p class="text-red-600 mr-1">※</p>
            <p>
              {{ $t("MemberFunction.Deposit.USDT2NoticeList1") }}
              {{ UsdtExchangeRate }} TWD
            </p>
          </div>
          <div class="mb-[1vh] flex">
            <p class="text-red-600 mr-1">※</p>
            <p>{{ $t("MemberFunction.Deposit.USDT2NoticeList2") }}</p>
          </div>
          <div class="mb-[1vh] flex">
            <p class="text-red-600 mr-1">※</p>
            <p>
              {{ $t("MemberFunction.Deposit.USDT2NoticeList3") }}
            </p>
          </div>
          <div class="mb-[1vh] flex">
            <p class="text-red-600 mr-1">※</p>
            <p>
              {{ $t("MemberFunction.Deposit.USDT2NoticeList4") }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </VForm>
</template>
